
import React, { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { History, Download, Eye, Trash2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import type { JogoGerado } from "@/types/softwareTurbinado";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

const UserBetsHistory: React.FC = () => {
  const { user } = useAuth();
  const [betsHistory, setBetsHistory] = useState<JogoGerado[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (user?.id) {
      loadBetsHistory();
    }
  }, [user?.id]);

  const loadBetsHistory = async () => {
    if (!user?.id) return;

    setIsLoading(true);
    try {
      const { data, error } = await supabase
        .from("jogos_gerados")
        .select("*")
        .eq("user_id", user.id)
        .order("created_at", { ascending: false });

      if (error) throw error;
      setBetsHistory(data || []);
    } catch (error) {
      console.error("Erro ao carregar histórico de apostas:", error);
      toast.error("Erro ao carregar histórico de apostas");
    } finally {
      setIsLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getTotalBets = (jogo: JogoGerado) => {
    return jogo.milhar.length + jogo.centenas.length + jogo.dezenas.length + 
           jogo.terno_dezenas.length + jogo.duques_dezenas.length + 
           jogo.duplas_grupo.length + jogo.ternos_grupo.length;
  };

  const deleteBet = async (jogoId: string) => {
    if (!confirm("Tem certeza que deseja excluir este jogo?")) return;

    try {
      const { error } = await supabase
        .from("jogos_gerados")
        .delete()
        .eq("id", jogoId)
        .eq("user_id", user.id);

      if (error) throw error;
      
      toast.success("Jogo excluído com sucesso!");
      await loadBetsHistory();
    } catch (error) {
      console.error("Erro ao excluir jogo:", error);
      toast.error("Erro ao excluir jogo");
    }
  };

  if (isLoading) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="text-center">Carregando histórico de apostas...</div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <History className="h-5 w-5" />
          Histórico de Apostas
        </CardTitle>
      </CardHeader>
      <CardContent>
        {betsHistory.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <History className="h-12 w-12 mx-auto mb-4 opacity-50" />
            <p>Nenhuma aposta gerada ainda.</p>
            <p className="text-sm">Gere seus primeiros jogos para ver o histórico aqui.</p>
          </div>
        ) : (
          <div className="space-y-4">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Data</TableHead>
                  <TableHead>Total de Jogos</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {betsHistory.map((jogo) => (
                  <TableRow key={jogo.id}>
                    <TableCell>
                      {formatDate(jogo.created_at)}
                    </TableCell>
                    <TableCell>
                      <Badge variant="secondary">
                        {getTotalBets(jogo)} jogos
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge className="bg-green-100 text-green-800 hover:bg-green-200">
                        Ativo
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            // Funcionalidade para visualizar detalhes
                            toast.info("Funcionalidade em desenvolvimento");
                          }}
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            // Funcionalidade para download
                            toast.info("Download em desenvolvimento");
                          }}
                        >
                          <Download className="h-4 w-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => deleteBet(jogo.id)}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            
            {/* Resumo detalhado */}
            <div className="mt-6 p-4 bg-gray-50 rounded-lg">
              <h4 className="font-semibold mb-3">Resumo das Apostas</h4>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                <div className="text-center">
                  <div className="font-bold text-lg text-blue-600">
                    {betsHistory.length}
                  </div>
                  <div className="text-gray-600">Jogos Gerados</div>
                </div>
                <div className="text-center">
                  <div className="font-bold text-lg text-green-600">
                    {betsHistory.reduce((acc, jogo) => acc + getTotalBets(jogo), 0)}
                  </div>
                  <div className="text-gray-600">Total de Apostas</div>
                </div>
                <div className="text-center">
                  <div className="font-bold text-lg text-purple-600">
                    {betsHistory.length * 5}
                  </div>
                  <div className="text-gray-600">Dias de Jogo</div>
                </div>
                <div className="text-center">
                  <div className="font-bold text-lg text-orange-600">
                    {betsHistory.length > 0 ? 'Ativo' : 'Inativo'}
                  </div>
                  <div className="text-gray-600">Status</div>
                </div>
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default UserBetsHistory;
